import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { cn } from "../../lib/utils.js";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../../Components/ui/collapsible.js";
import { ChevronDown, X } from "lucide-react";
import { SearchQueryPill } from "./SearchQueryPill.js";
import { ChainOfThoughtWidget } from "@brandclub/bc-widgets";
import { useConciergeContext } from "../ConciergeContextProvider.js";
const SearchAngleDialog = ({
  detail,
  onClose
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999
      },
      onClick: onClose,
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "24px",
            maxWidth: "680px",
            width: "90%",
            maxHeight: "80vh",
            overflow: "auto",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            fontFamily: "monospace",
            fontSize: "13px",
            lineHeight: "1.5"
          },
          onClick: (e) => e.stopPropagation(),
          children: [
            /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }, children: [
              /* @__PURE__ */ jsx("h3", { style: { margin: 0, fontSize: "16px", fontFamily: "sans-serif", fontWeight: 600 }, children: detail.title }),
              /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: onClose,
                  style: {
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center"
                  },
                  children: /* @__PURE__ */ jsx(X, { size: 18 })
                }
              )
            ] }),
            detail.description && /* @__PURE__ */ jsx("p", { style: { margin: "0 0 16px", fontFamily: "sans-serif", fontSize: "14px", color: "#666" }, children: detail.description }),
            detail.parts && /* @__PURE__ */ jsxs("div", { style: { marginBottom: "16px" }, children: [
              /* @__PURE__ */ jsx("div", { style: { fontFamily: "sans-serif", fontSize: "12px", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }, children: "LLM Parts" }),
              /* @__PURE__ */ jsx("pre", { style: { background: "#f5f5f5", padding: "12px", borderRadius: "8px", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }, children: JSON.stringify(detail.parts, null, 2) })
            ] }),
            /* @__PURE__ */ jsxs("div", { style: { marginBottom: "16px" }, children: [
              /* @__PURE__ */ jsx("div", { style: { fontFamily: "sans-serif", fontSize: "12px", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }, children: "Plain Text Query (Phase 1)" }),
              /* @__PURE__ */ jsx("pre", { style: { background: "#e8f5e9", padding: "12px", borderRadius: "8px", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }, children: detail.textQuery })
            ] }),
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("div", { style: { fontFamily: "sans-serif", fontSize: "12px", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }, children: "Embedding Query (Phase 2 KNN)" }),
              /* @__PURE__ */ jsx("pre", { style: { background: "#e3f2fd", padding: "12px", borderRadius: "8px", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }, children: detail.embeddingQuery })
            ] })
          ]
        }
      )
    }
  );
};
const SearchIcon = ({ className, size = 14, color }) => /* @__PURE__ */ jsxs(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 14 14",
    fill: "none",
    className,
    style: color ? { color } : void 0,
    children: [
      /* @__PURE__ */ jsx(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M5.88248 0C9.13129 0 11.765 2.63362 11.765 5.88234C11.765 7.22364 11.316 8.46008 10.5602 9.44961L13.1039 11.9944C13.4102 12.3007 13.4102 12.7973 13.1039 13.1036C12.8212 13.3863 12.3763 13.4081 12.0686 13.1689L11.9947 13.1036L9.44983 10.56C8.46027 11.3158 7.2238 11.7647 5.88248 11.7647C2.63367 11.7647 0 9.1311 0 5.88237C0 2.63364 2.63368 2.7351e-05 5.88248 2.7351e-05V0Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M5.8927 2.4707C7.777 2.47088 9.30481 3.99847 9.30481 5.88281C9.30463 7.767 7.77689 9.29474 5.8927 9.29492C4.00836 9.29492 2.48077 7.76711 2.48059 5.88281C2.48059 3.99836 4.00825 2.4707 5.8927 2.4707Z",
          className: "tw:fill-background tw:stroke-background",
          strokeWidth: "1.33333"
        }
      )
    ]
  }
);
const ProductSearchQueriesSectionInner = ({
  previewSearchQueries: previewSearchQueriesProp,
  previewQueryProducts,
  isStreaming,
  currentStatus,
  hasContent,
  isLastMessage = false,
  styles,
  resolveColor
}) => {
  const previewSearchQueries = Array.isArray(previewSearchQueriesProp) ? previewSearchQueriesProp : [];
  const { searchAngleDetails } = useConciergeContext();
  const isDevMode = new URLSearchParams(window.location.search).get("isDev") === "true";
  const [selectedAngle, setSelectedAngle] = useState(null);
  const [animatedQueryIndices, setAnimatedQueryIndices] = useState(/* @__PURE__ */ new Set());
  const [lastProcessedLength, setLastProcessedLength] = useState(0);
  const [hasEverStreamed, setHasEverStreamed] = useState(false);
  const [isOpen, setIsOpen] = useState(styles.defaultExpanded);
  const userHasInteracted = useRef(false);
  const handleOpenChange = (open) => {
    userHasInteracted.current = true;
    setIsOpen(open);
  };
  useEffect(() => {
    if (isStreaming) {
      setHasEverStreamed(true);
    }
  }, [isStreaming]);
  useEffect(() => {
    if (!isStreaming || !styles.animationsEnabled) {
      setAnimatedQueryIndices(/* @__PURE__ */ new Set());
      return;
    }
    if (previewQueryProducts.length > lastProcessedLength) {
      const newProducts = previewQueryProducts.slice(lastProcessedLength);
      newProducts.forEach((queryData) => {
        setAnimatedQueryIndices((prev) => /* @__PURE__ */ new Set([...prev, queryData.queryIndex]));
        setTimeout(() => {
          setAnimatedQueryIndices((prev) => {
            const next = new Set(prev);
            next.delete(queryData.queryIndex);
            return next;
          });
        }, styles.productAnimationDuration + 100);
      });
      setLastProcessedLength(previewQueryProducts.length);
    }
  }, [
    previewQueryProducts.length,
    isStreaming,
    lastProcessedLength,
    styles.animationsEnabled,
    styles.productAnimationDuration
  ]);
  const allQueriesHaveProducts = previewSearchQueries.length > 0 && previewSearchQueries.every((_, idx) => previewQueryProducts.some((qp) => qp.queryIndex === idx));
  useEffect(() => {
    if (styles.autoCollapse && hasEverStreamed && allQueriesHaveProducts && hasContent && !userHasInteracted.current) {
      const timer = setTimeout(() => {
        setIsOpen(false);
      }, styles.autoCollapseDelay);
      return () => clearTimeout(timer);
    }
  }, [hasEverStreamed, allQueriesHaveProducts, hasContent, styles.autoCollapse, styles.autoCollapseDelay]);
  useEffect(() => {
    if (!isStreaming && !hasEverStreamed && previewSearchQueries.length > 0) {
      setIsOpen(false);
    }
  }, [isStreaming, hasEverStreamed, previewSearchQueries.length]);
  const fontWeightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  };
  return /* @__PURE__ */ jsxs(
    Collapsible,
    {
      open: isOpen,
      onOpenChange: handleOpenChange,
      className: "tw:w-full",
      style: { marginBottom: `${styles.containerMarginBottom}px` },
      children: [
        /* @__PURE__ */ jsxs(
          CollapsibleTrigger,
          {
            className: "tw:flex tw:items-center tw:cursor-pointer tw:group",
            style: {
              gap: `${styles.headerGap}px`,
              marginBottom: `${styles.headerMarginBottom}px`
            },
            children: [
              /* @__PURE__ */ jsx(SearchIcon, { size: styles.headerIconSize, color: resolveColor(styles.headerIconColor) }),
              /* @__PURE__ */ jsx(
                "span",
                {
                  style: {
                    fontSize: `${styles.headerLabelFontSize}px`,
                    fontWeight: fontWeightMap[styles.headerLabelFontWeight] || 500,
                    fontFamily: styles.headerLabelFontFamily,
                    color: resolveColor(styles.headerLabelColor)
                  },
                  children: styles.headerLabelText
                }
              ),
              /* @__PURE__ */ jsx(
                ChevronDown,
                {
                  style: {
                    width: `${styles.headerChevronSize}px`,
                    height: `${styles.headerChevronSize}px`,
                    color: resolveColor(styles.headerChevronColor),
                    transition: `transform ${styles.chevronRotationDuration}ms`,
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
                  }
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(CollapsibleContent, { forceMount: true, className: cn(!isOpen && "tw:hidden"), children: /* @__PURE__ */ jsx(
          "div",
          {
            className: "tw:flex tw:flex-col tw:items-start",
            style: {
              gap: `${styles.pillsGap}px`,
              marginBottom: `${styles.pillsSectionMarginBottom}px`
            },
            children: previewSearchQueries.map((query, idx) => {
              const matchingQueryData = previewQueryProducts.find((qp) => qp.queryIndex === idx);
              const isLoading = isStreaming && !matchingQueryData;
              const isAnimating = matchingQueryData ? animatedQueryIndices.has(matchingQueryData.queryIndex) : false;
              const angleDetail = searchAngleDetails[String(idx)];
              return /* @__PURE__ */ jsx(
                "div",
                {
                  onClick: () => {
                    if (isDevMode && angleDetail) {
                      setSelectedAngle(angleDetail);
                    }
                  },
                  style: { cursor: isDevMode && angleDetail ? "pointer" : "default" },
                  children: /* @__PURE__ */ jsx(
                    SearchQueryPill,
                    {
                      query,
                      products: (matchingQueryData == null ? void 0 : matchingQueryData.products) ?? [],
                      totalResults: (matchingQueryData == null ? void 0 : matchingQueryData.totalResults) ?? 0,
                      isLoading,
                      isAnimating
                    }
                  )
                },
                `${query}-${idx}`
              );
            })
          }
        ) }),
        isStreaming && currentStatus && /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              fontSize: `${styles.headerLabelFontSize}px`,
              fontWeight: fontWeightMap[styles.headerLabelFontWeight] || 500,
              fontFamily: styles.headerLabelFontFamily,
              color: resolveColor(styles.headerLabelColor),
              marginTop: "8px"
            },
            children: currentStatus
          }
        ),
        isDevMode && selectedAngle && /* @__PURE__ */ jsx(SearchAngleDialog, { detail: selectedAngle, onClose: () => setSelectedAngle(null) })
      ]
    }
  );
};
const ProductSearchQueriesSection = ({
  previewSearchQueries: previewSearchQueriesProp,
  previewQueryProducts,
  isStreaming,
  currentStatus,
  hasContent,
  isLastMessage = false
}) => {
  const previewSearchQueries = Array.isArray(previewSearchQueriesProp) ? previewSearchQueriesProp : [];
  if (previewSearchQueries.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(ChainOfThoughtWidget, { widgetId: "chain-of-thought", displayName: "Search Results", children: (styles, resolveColor) => /* @__PURE__ */ jsx("div", { style: { width: "100%" }, children: /* @__PURE__ */ jsx(
    ProductSearchQueriesSectionInner,
    {
      previewSearchQueries,
      previewQueryProducts,
      isStreaming,
      currentStatus,
      hasContent,
      isLastMessage,
      styles,
      resolveColor
    }
  ) }) });
};
export {
  ProductSearchQueriesSection
};
//# sourceMappingURL=ProductSearchQueriesSection.js.map
